// ---------------------------------------------------------------------------
// เอกสารภาษี (e-Tax) — ใบกำกับภาษี / ใบเสร็จรับเงิน-ใบกำกับภาษี / ใบลดหนี้
// ---------------------------------------------------------------------------
// สถานะ  draft -> issued -> pending_rd (รอส่งสรรพากร/อยู่ระหว่างส่ง)   และ cancelled
//   - draft ไม่มีเลขที่ แก้ไขได้ · ออกเอกสาร (issue) = ขอเลขเรียง + ล็อกข้อมูล แก้ไม่ได้อีก
//   - ยกเลิกต้องมีเหตุผล ไม่ลบแถว เลขที่ไม่ถูกนำกลับมาใช้ · หลังส่งสรรพากรแล้ว (pending_rd) ต้องออกใบลดหนี้แทน
//   - ใบลดหนี้อ้างใบเดิม ยอดสะสมของใบลดหนี้ที่ไม่ถูกยกเลิก รวมแล้วต้องไม่เกินยอดใบเดิม
//   - ใบแจ้งหนี้ 1 ใบ : เอกสารภาษี active ได้ 1 ใบ (ไม่นับใบลดหนี้ และใบที่ยกเลิกแล้ว)
//   - ยังไม่ได้ส่งเข้ากรมสรรพากรจริง — ดู etaxProvider.js
// ---------------------------------------------------------------------------
import { taxDocumentRepo, customerRepo, invoiceRepo, receiptRepo } from "../mock/repositories";
import { nextDocNo } from "./docNumber";
import { logActivity } from "./activityLog";
import { etaxProvider } from "./etaxProvider";
import { calcVat, normalizeBranch, validateThaiTaxId, VAT_RATE } from "../utils/thaiTax";

export const TAX_DOC_TYPES = {
  tax_invoice: { label: "ใบกำกับภาษี", labelEn: "Tax invoice" },
  receipt_tax_invoice: { label: "ใบเสร็จรับเงิน/ใบกำกับภาษี", labelEn: "Receipt / Tax invoice" },
  credit_note: { label: "ใบลดหนี้", labelEn: "Credit note" },
};

export const TAX_STATUS = { draft: "draft", issued: "issued", pendingRd: "pending_rd", cancelled: "cancelled" };

const ACTIVE = [TAX_STATUS.draft, TAX_STATUS.issued, TAX_STATUS.pendingRd];
const LOCKED = [TAX_STATUS.issued, TAX_STATUS.pendingRd, TAX_STATUS.cancelled];

export class TaxDocError extends Error {
  constructor(message) {
    super(message);
    this.name = "TaxDocError";
  }
}

// --------------------------------------------------------------------------
// snapshot ผู้ขาย / ผู้ซื้อ
// --------------------------------------------------------------------------
export const sellerOf = (organization) => ({
  name: organization?.name || "",
  nameEn: organization?.nameEn || "",
  taxId: String(organization?.taxId || "").trim(),
  branchCode: normalizeBranch(organization?.branchCode) || "00000",
  branchName: organization?.branchName || "",
  address: organization?.address || "",
  phone: organization?.phone || "",
  vatRegistered: !!organization?.vatRegistered,
});

export const buyerOf = (customer) => ({
  name: customer?.taxName || customer?.name || "",
  taxId: String(customer?.taxId || "").trim(),
  branchCode: normalizeBranch(customer?.branchCode) || "00000",
  address: customer?.taxAddress || customer?.address || "",
  isJuristic: !!customer?.isJuristic,
});

/** ทำความสะอาดผู้ซื้อที่กรอกมือ: เลขล้วน, สาขา 5 หลัก (ผิดรูปแบบคงค่าเดิมไว้ให้ตรวจเจอตอนออกเอกสาร) */
const cleanBuyer = (buyer) => {
  const out = { ...buyer };
  if (out.taxId !== undefined) out.taxId = String(out.taxId || "").replace(/\D/g, "");
  // ไม่กรอกสาขา = สำนักงานใหญ่ เก็บ "00000" ชัด ๆ (ผิดรูปแบบคงค่าเดิมไว้ให้ตรวจเจอตอนออก)
  out.branchCode = normalizeBranch(out.branchCode ?? "") || out.branchCode;
  return out;
};

/** ปัญหาของข้อมูลผู้ขาย (ว่าง = ออกเอกสารได้) */
export const sellerProblems = (seller) => {
  const problems = [];
  if (!seller?.vatRegistered) problems.push("ร้านยังไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม (VAT) — ออกได้เฉพาะใบเสร็จรับเงินธรรมดา");
  if (!validateThaiTaxId(seller?.taxId)) problems.push("เลขประจำตัวผู้เสียภาษีของร้านไม่ถูกต้อง (ตรวจที่หน้าข้อมูลองค์กร)");
  if (!String(seller?.name || "").trim()) problems.push("ยังไม่ได้กรอกชื่อร้าน");
  if (!String(seller?.address || "").trim()) problems.push("ยังไม่ได้กรอกที่อยู่ร้าน");
  return problems;
};

/** ปัญหาของข้อมูลผู้ซื้อ */
export const buyerProblems = (buyer) => {
  const problems = [];
  if (!String(buyer?.name || "").trim()) problems.push("ยังไม่ได้กรอกชื่อผู้ซื้อ");
  if (!String(buyer?.address || "").trim()) problems.push("ยังไม่ได้กรอกที่อยู่ผู้ซื้อ");
  const taxId = String(buyer?.taxId || "").trim();
  if (taxId && !validateThaiTaxId(taxId)) problems.push("เลขประจำตัวผู้เสียภาษีของผู้ซื้อไม่ถูกต้อง");
  if (buyer?.isJuristic && !taxId) problems.push("ผู้ซื้อเป็นนิติบุคคล ต้องกรอกเลขประจำตัวผู้เสียภาษี");
  if (normalizeBranch(buyer?.branchCode) === null) problems.push("รหัสสาขาผู้ซื้อต้องเป็นตัวเลขไม่เกิน 5 หลัก");
  return problems;
};

// --------------------------------------------------------------------------
// คำนวณยอด
// --------------------------------------------------------------------------
const cleanItems = (items) =>
  (items || [])
    .map((item) => ({
      name: String(item.name || "").trim(),
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 0,
    }))
    .filter((item) => item.name && item.quantity > 0);

export const amountsOf = ({ items, discount = 0, priceMode = "exclusive", rate = VAT_RATE }) => {
  const rows = cleanItems(items);
  const calc = calcVat({ lines: rows, discount, mode: priceMode, rate });
  return {
    items: rows.map((row) => ({ ...row, total: Math.round(row.price * 100 * row.quantity) / 100 })),
    priceMode,
    vatRate: rate,
    subtotal: calc.subtotal,
    discount: calc.discount,
    vatBase: calc.base,
    vatAmount: calc.vat,
    total: calc.total,
  };
};

const sumSatang = (rows) => rows.reduce((sum, row) => sum + Math.round((Number(row.total) || 0) * 100), 0);
const toSatang = (baht) => Math.round((Number(baht) || 0) * 100);

// --------------------------------------------------------------------------
// อ่าน
// --------------------------------------------------------------------------
const loadDoc = async (orgId, id) => {
  const doc = await taxDocumentRepo.get(orgId, id);
  if (!doc) throw new TaxDocError("ไม่พบเอกสารภาษีนี้");
  return doc;
};

const assertDraft = (doc) => {
  if (doc.status !== TAX_STATUS.draft) {
    throw new TaxDocError("เอกสารที่ออกแล้วแก้ไขไม่ได้ — หากข้อมูลผิดให้ยกเลิกแล้วออกใหม่ หรือออกใบลดหนี้");
  }
};

/** ใบ active (ยังไม่ยกเลิก) ที่อ้างใบแจ้งหนี้นี้ — ไม่นับใบลดหนี้ */
const activeForInvoice = async (orgId, invoiceId, exceptId = "") =>
  taxDocumentRepo.all(
    orgId,
    (row) => row.invoiceId === invoiceId && row.docType !== "credit_note" && row.status !== TAX_STATUS.cancelled && row.id !== exceptId
  );

export const taxDocumentService = {
  /** ใบภาษี active ของใบแจ้งหนี้ (null = ยังไม่มี) */
  activeForInvoice: async (orgId, invoiceId) => (await activeForInvoice(orgId, invoiceId))[0] || null,

  /** ผลรวมใบลดหนี้ที่ไม่ถูกยกเลิกของใบเดิม (บาท) */
  creditedTotal: async (orgId, originalId, exceptId = "") => {
    const rows = await taxDocumentRepo.all(
      orgId,
      (row) => row.docType === "credit_note" && row.refDocId === originalId && row.status !== TAX_STATUS.cancelled && row.id !== exceptId
    );
    return sumSatang(rows) / 100;
  },

  // ------------------------------------------------------------------------
  // สร้างฉบับร่าง
  // ------------------------------------------------------------------------
  /**
   * source = "invoice" | "receipt" | "manual"
   *   invoice/receipt : ดึงรายการจากใบแจ้งหนี้ (ต้องชำระแล้วถ้าเป็นใบเสร็จรับเงิน/ใบกำกับภาษี)
   *   manual          : ส่ง items/buyer/customer เอง
   */
  createDraft: async ({ orgId, organization, source = "manual", invoice, receipt, customer, docType, items, discount = 0, priceMode = "exclusive", buyer, note = "", by = "" }) => {
    const seller = sellerOf(organization);
    if (!seller.vatRegistered) {
      throw new TaxDocError("ร้านยังไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม (VAT) — ออกได้เฉพาะใบเสร็จรับเงินธรรมดา");
    }

    let inv = invoice || null;
    let rcp = receipt || null;
    if (source === "receipt" && rcp && !inv) inv = await invoiceRepo.get(orgId, rcp.invoiceId);
    if (source === "invoice" && inv && !rcp) rcp = (await receiptRepo.all(orgId, (row) => row.invoiceId === inv.id))[0] || null;
    if (source !== "manual" && !inv) throw new TaxDocError("ไม่พบใบแจ้งหนี้ต้นทางของเอกสารนี้");

    const type = docType || (source === "manual" ? "tax_invoice" : "receipt_tax_invoice");
    if (!["tax_invoice", "receipt_tax_invoice"].includes(type)) throw new TaxDocError("ประเภทเอกสารไม่ถูกต้อง (ใบลดหนี้ให้สร้างจากใบเดิม)");

    if (inv) {
      if (type === "receipt_tax_invoice" && inv.status !== "paid") {
        throw new TaxDocError("ใบแจ้งหนี้นี้ยังไม่ได้ชำระ — ออกใบเสร็จรับเงิน/ใบกำกับภาษีไม่ได้");
      }
      const existing = await activeForInvoice(orgId, inv.id);
      if (existing.length) {
        throw new TaxDocError(`ใบแจ้งหนี้ ${inv.docNo} มีเอกสารภาษีอยู่แล้ว (${existing[0].docNo || "ฉบับร่าง"}) — ยกเลิกใบเดิมก่อนถ้าต้องการออกใหม่`);
      }
    }

    const cust = customer || (inv?.customerId ? await customerRepo.get(orgId, inv.customerId) : null);
    const lines = inv ? inv.items : items;
    const amounts = amountsOf({
      items: lines,
      discount: inv ? inv.discount : discount,
      priceMode: inv ? "exclusive" : priceMode,
      rate: inv?.vatPercent ?? VAT_RATE,
    });
    if (!amounts.items.length || amounts.total <= 0) throw new TaxDocError("เอกสารต้องมีรายการและยอดมากกว่า 0");

    const row = await taxDocumentRepo.create(orgId, {
      docNo: "",
      docType: type,
      status: TAX_STATUS.draft,
      invoiceId: inv?.id || "",
      invoiceNo: inv?.docNo || "",
      receiptId: rcp?.id || "",
      receiptNo: rcp?.docNo || "",
      transactionId: rcp?.transactionId || inv?.transactionId || "",
      customerId: cust?.id || inv?.customerId || "",
      seller,
      buyer: cleanBuyer({ ...buyerOf(cust), ...(buyer || {}) }),
      ...amounts,
      // ยอดใบแจ้งหนี้เดิมเก็บแบบปัดเป็นบาท (เอกสารเก่า) — ถ้าต่างจากที่คำนวณใหม่ ให้ร้านเห็นก่อนกดออก
      sourceTotal: inv ? Number(inv.total) || 0 : null,
      note,
      createdBy: by,
    });
    return row;
  },

  /** แก้ฉบับร่าง (ผู้ซื้อ/รายการ/ส่วนลด/หมายเหตุ) — เอกสารที่ออกแล้วแก้ไม่ได้ */
  updateDraft: async (orgId, id, patch = {}) => {
    const doc = await loadDoc(orgId, id);
    assertDraft(doc);
    const next = {};
    if (patch.buyer) next.buyer = cleanBuyer({ ...doc.buyer, ...patch.buyer });
    if (patch.note !== undefined) next.note = patch.note;
    if (doc.docType === "credit_note") {
      if (patch.reason !== undefined) next.reason = patch.reason;
    } else if (patch.items || patch.discount !== undefined || patch.priceMode) {
      Object.assign(
        next,
        amountsOf({
          items: patch.items || doc.items,
          discount: patch.discount !== undefined ? patch.discount : doc.discount,
          priceMode: patch.priceMode || doc.priceMode,
          rate: doc.vatRate,
        })
      );
      if (!next.items.length || next.total <= 0) throw new TaxDocError("เอกสารต้องมีรายการและยอดมากกว่า 0");
    }
    return taxDocumentRepo.update(orgId, id, next);
  },

  // ------------------------------------------------------------------------
  // ออกเอกสาร (ขอเลขที่เรียง + ล็อก)
  // ------------------------------------------------------------------------
  issue: async ({ orgId, organization, id, by = "" }) => {
    const doc = await loadDoc(orgId, id);
    if (doc.status !== TAX_STATUS.draft) throw new TaxDocError("เอกสารนี้ออกไปแล้วหรือถูกยกเลิก");

    const seller = sellerOf(organization);
    const problems = [...sellerProblems(seller), ...buyerProblems(doc.buyer)];
    if (problems.length) throw new TaxDocError(problems.join(" · "));

    if (doc.docType === "credit_note") {
      const original = await loadDoc(orgId, doc.refDocId);
      if (![TAX_STATUS.issued, TAX_STATUS.pendingRd].includes(original.status)) throw new TaxDocError("ใบเดิมไม่อยู่ในสถานะที่ออกใบลดหนี้ได้");
      const credited = await taxDocumentService.creditedTotal(orgId, original.id, doc.id);
      if (toSatang(credited) + toSatang(doc.total) > toSatang(original.total)) throw new TaxDocError("ยอดใบลดหนี้สะสมเกินยอดใบเดิม");
    } else if (doc.invoiceId) {
      const others = await activeForInvoice(orgId, doc.invoiceId, doc.id);
      if (others.length) throw new TaxDocError(`ใบแจ้งหนี้ ${doc.invoiceNo} มีเอกสารภาษีอื่นอยู่แล้ว (${others[0].docNo || "ฉบับร่าง"})`);
    }

    const { docNo } = await nextDocNo({ orgId, organization, docType: doc.docType });
    const issued = await taxDocumentRepo.update(orgId, id, {
      docNo,
      status: TAX_STATUS.issued,
      issuedAt: new Date().toISOString(),
      issuedBy: by,
      seller,
      rdStatus: "not_sent",
    });
    await logActivity(orgId, {
      type: "payment",
      title: `ออก${TAX_DOC_TYPES[doc.docType].label}`,
      detail: docNo,
      customerId: doc.customerId,
      customerName: doc.buyer?.name,
      amount: doc.total,
      by,
    });
    return issued;
  },

  // ------------------------------------------------------------------------
  // ยกเลิก (ต้องมีเหตุผล ไม่ลบแถว)
  // ------------------------------------------------------------------------
  cancel: async ({ orgId, id, reason, by = "" }) => {
    const text = String(reason || "").trim();
    if (!text) throw new TaxDocError("กรุณาระบุเหตุผลที่ยกเลิก");
    const doc = await loadDoc(orgId, id);
    if (doc.status === TAX_STATUS.cancelled) throw new TaxDocError("เอกสารนี้ถูกยกเลิกไปแล้ว");
    if (doc.status === TAX_STATUS.pendingRd) throw new TaxDocError("เอกสารนี้อยู่ในขั้นตอนส่งกรมสรรพากรแล้ว — ให้ออกใบลดหนี้แทนการยกเลิก");

    if (doc.docType !== "credit_note") {
      const credited = await taxDocumentRepo.all(orgId, (row) => row.refDocId === doc.id && row.status !== TAX_STATUS.cancelled);
      if (credited.length) throw new TaxDocError("มีใบลดหนี้อ้างอิงเอกสารนี้อยู่ — ยกเลิกใบลดหนี้ก่อน");
    }

    const cancelled = await taxDocumentRepo.update(orgId, id, {
      status: TAX_STATUS.cancelled,
      cancelReason: text,
      cancelledAt: new Date().toISOString(),
      cancelledBy: by,
      cancelledFrom: doc.status,
    });
    if (doc.docNo) {
      await logActivity(orgId, {
        type: "payment",
        title: `ยกเลิก${TAX_DOC_TYPES[doc.docType].label}`,
        detail: doc.docNo,
        customerId: doc.customerId,
        customerName: doc.buyer?.name,
        by,
      });
    }
    return cancelled;
  },

  // ------------------------------------------------------------------------
  // ใบลดหนี้
  // ------------------------------------------------------------------------
  /** amount = ยอดลดหนี้รวม VAT แล้ว (บาท) */
  createCreditNote: async ({ orgId, organization, originalId, amount, reason, by = "" }) => {
    const text = String(reason || "").trim();
    if (!text) throw new TaxDocError("กรุณาระบุเหตุผลการลดหนี้");
    const seller = sellerOf(organization);
    if (!seller.vatRegistered) throw new TaxDocError("ร้านยังไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม (VAT)");

    const original = await loadDoc(orgId, originalId);
    if (original.docType === "credit_note") throw new TaxDocError("ออกใบลดหนี้ซ้อนใบลดหนี้ไม่ได้");
    if (![TAX_STATUS.issued, TAX_STATUS.pendingRd].includes(original.status)) throw new TaxDocError("ออกใบลดหนี้ได้เฉพาะจากเอกสารที่ออกแล้วและไม่ถูกยกเลิก");

    const calc = calcVat({ lines: [{ price: Number(amount) || 0, quantity: 1 }], mode: "inclusive", rate: original.vatRate });
    if (calc.totalSatang <= 0) throw new TaxDocError("ยอดลดหนี้ต้องมากกว่า 0");

    const credited = await taxDocumentService.creditedTotal(orgId, original.id);
    const remainSatang = toSatang(original.total) - toSatang(credited);
    if (calc.totalSatang > remainSatang) {
      throw new TaxDocError(`ยอดลดหนี้เกินยอดคงเหลือของใบเดิม (คงเหลือลดหนี้ได้ ${(remainSatang / 100).toFixed(2)} บาท)`);
    }

    // ลดหนี้เต็มยอดใบเดิม -> ใช้ฐานภาษี/VAT ตรงกับใบเดิม ไม่ให้ต่างกันเพราะการปัดเศษ
    const fullOriginal = calc.totalSatang === toSatang(original.total);
    const line = { name: `ลดหนี้ตาม ${original.docNo}: ${text}`, price: calc.total, quantity: 1, total: calc.total };

    return taxDocumentRepo.create(orgId, {
      docNo: "",
      docType: "credit_note",
      status: TAX_STATUS.draft,
      refDocId: original.id,
      refDocNo: original.docNo,
      refDocType: original.docType,
      refDocDate: original.issuedAt,
      refDocTotal: original.total,
      invoiceId: original.invoiceId,
      invoiceNo: original.invoiceNo,
      customerId: original.customerId,
      seller,
      buyer: original.buyer,
      reason: text,
      items: [line],
      priceMode: "inclusive",
      vatRate: original.vatRate,
      subtotal: calc.total,
      discount: 0,
      vatBase: fullOriginal ? original.vatBase : calc.base,
      vatAmount: fullOriginal ? original.vatAmount : calc.vat,
      total: calc.total,
      createdBy: by,
    });
  },

  // ------------------------------------------------------------------------
  // ส่งกรมสรรพากร (ยังไม่ต่อผู้ให้บริการ -> ไม่เปลี่ยนสถานะ)
  // ------------------------------------------------------------------------
  submitToRd: async ({ orgId, id }) => {
    const doc = await loadDoc(orgId, id);
    if (doc.status !== TAX_STATUS.issued) throw new TaxDocError("ส่งได้เฉพาะเอกสารที่ออกแล้วและยังไม่ถูกส่ง");
    const result = await etaxProvider.submit(doc);
    if (result?.status === "queued") {
      await taxDocumentRepo.update(orgId, id, { status: TAX_STATUS.pendingRd, rdStatus: "queued", rdRef: result.providerRef || "", rdSubmittedAt: new Date().toISOString() });
    }
    return result;
  },
};

export const isLocked = (doc) => LOCKED.includes(doc?.status);
export const isActiveDoc = (doc) => ACTIVE.includes(doc?.status);
export const taxProviderReady = () => etaxProvider.isConfigured();
